import { createContext, useContext, useState, useMemo, ReactNode } from "react"

// ============================================================================
// TYPES
// ============================================================================

export type ContextPanelContextType = {
  /** Currently active panel ID */
  activePanel: string | undefined
  /** Whether the context panel area is visible */
  visible: boolean

  /** Set the active panel (undefined to close) */
  setActive: (id: string | undefined) => void
  /** Show/hide the context panel area */
  show: (visible: boolean) => void
}

// ============================================================================
// CONTEXT
// ============================================================================

const ContextPanelContext = createContext<ContextPanelContextType | null>(null)

// ============================================================================
// PROVIDER
// ============================================================================

/**
 * Provider for the right contextual panel (sidebar).
 * 
 * This manages the visibility and active panel state for the right sidebar.
 * The actual panel definitions are passed to the ContextSidebar component
 * based on the active tab type (e.g., workspace tabs show tasks/users/memories,
 * settings tabs show different panels).
 */
export function ContextPanelProvider({ children }: { children: ReactNode }) {
  const [activePanel, setActivePanel] = useState<string | undefined>(undefined)
  const [visible, setVisible] = useState(false)

  const api = useMemo<ContextPanelContextType>(() => ({
    activePanel,
    visible,

    setActive: (id: string | undefined) => {
      setActivePanel(id)
      // Auto-show when selecting a panel, auto-hide when deselecting
      if (id !== undefined) {
        setVisible(true)
      }
    },

    show: (visible: boolean) => {
      setVisible(visible)
      // Clear active panel when hiding
      if (!visible) {
        setActivePanel(undefined)
      }
    },
  }), [activePanel, visible])

  return (
    <ContextPanelContext.Provider value={api}>
      {children}
    </ContextPanelContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access contextual panel (right sidebar) state and methods.
 * 
 * @example
 * const contextPanel = useContextPanel()
 * contextPanel.setActive("tasks")  // Opens tasks panel
 * contextPanel.setActive(undefined)  // Closes panel
 */
export function useContextPanel(): ContextPanelContextType {
  const ctx = useContext(ContextPanelContext)
  if (!ctx) {
    throw new Error("useContextPanel must be used within a ContextPanelProvider")
  }
  return ctx
}
